import React from 'react';

import PropTypes from 'prop-types';

import { useDeprecate } from '@adsk/alloy-react-helpers';
import ProgressRing from '@adsk/alloy-react-progress-ring';
import theme, {
  getInteractiveStyles,
  stylePropType,
  StylableComponent,
} from '@adsk/alloy-react-theme';

import { GetLabelProps, GetToggleButtonProps, TargetProps } from '../../types';
import ToggleButton from './ToggleButton';

type ControlLabelProps = StylableComponent<HTMLLabelElement> &
  GetToggleButtonProps &
  GetLabelProps & {
    /** @deprecated use disabled field instead */
    isDisabled?: boolean;
    disabled?: boolean;
    isLoading?: boolean;
    /** @deprecated use error field instead */
    isInvalid?: boolean;
    error?: boolean;
    isOpen: boolean;
    targetProps: TargetProps<HTMLLabelElement>;
  };

const ControlLabel: React.FC<ControlLabelProps> = ({
  className,
  style,
  isDisabled = false,
  disabled,
  isLoading = false,
  isInvalid = false,
  error,
  isOpen = false,
  getLabelProps,
  getToggleButtonProps,
  targetProps,
  children,
  ...props
}) => {
  useDeprecate(isDisabled, 'Dropdown Control', 'isDisabled', 'disabled');
  useDeprecate(isInvalid, 'Dropdown Control', 'isInvalid', 'error');

  disabled = disabled !== undefined ? disabled : isDisabled;
  error = error !== undefined ? error : isInvalid;
  return (
    <label
      ref={targetProps.ref}
      data-testid={'Dropdown__control'}
      className={className}
      css={[
        {
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '1px 12px',
          border: `1px solid ${theme.colors.charcoal400}`,
          borderRadius: 2,
          cursor: 'pointer',
          ...getInteractiveStyles(disabled),

          ...(isOpen && {
            borderColor: theme.colors.adskBlue500,
            boxShadow: `0 0 0 2px ${theme.colors.focusHalo}`,

            '&:hover': {
              boxShadow: `0 0 0 2px ${theme.colors.focusHalo}`,
            },
          }),

          ...(disabled && {
            backgroundColor: theme.colors.charcoal050,
            borderColor: theme.colors.charcoal200,
            color: theme.colors.charcoal500,
            cursor: 'default',
            pointerEvents: 'none',
          }),

          ...(error &&
            !disabled && {
              borderColor: theme.colors.red500,
            }),
        },
        style,
      ]}
      {...getLabelProps?.()}
      {...props}
    >
      {children}
      {isLoading && (
        <ProgressRing
          size={ProgressRing.SIZES.XSMALL}
          style={{ marginLeft: 8 }}
        />
      )}
      <ToggleButton
        getToggleButtonProps={getToggleButtonProps}
        isOpen={isOpen}
        disabled={disabled}
        style={{
          marginLeft: 8,
          marginRight: 4,
          ...(disabled && { color: theme.colors.charcoal500 }),
        }}
      />
    </label>
  );
};

ControlLabel.displayName = 'ControlLabel';

ControlLabel.propTypes = {
  /** Styles applied to the root element */
  style: stylePropType,
  /** Class applied to the root element */
  className: PropTypes.string,
  /** Toggle disabled state */
  disabled: PropTypes.bool,
  /** **DEPRECATED** use disabled field instead.  Toggle disabled state */
  isDisabled: PropTypes.bool,
  /** Toggle loading state */
  isLoading: PropTypes.bool,
  /** **DEPRECATED** use error field instead.  Toggle error state */
  isInvalid: PropTypes.bool,
  /** Toggle error state */
  error: PropTypes.bool,
  /** Whether Dropdown menu is open */
  isOpen: PropTypes.bool.isRequired,
  /** Function to get props for the menu toggle button */
  getToggleButtonProps: PropTypes.func.isRequired,
  /** Function to get props for the label surrounding the control */
  getLabelProps: PropTypes.func,
  /** targetProps returned from useDropdown hook */
  targetProps: PropTypes.shape({
    ref: PropTypes.any,
  }).isRequired,
  /** The content of te ControlContainer */
  children: PropTypes.node,
};

export default ControlLabel;
